import { useState, useEffect, useRef } from 'react';
import API from '@/lib/api';
import IOSToggle from '@/components/IOSToggle';
import { Monitor, Image as ImageIcon, Trash, Plus, Copy, ArrowSquareOut, CheckCircle } from '@phosphor-icons/react';

const COLOR_PRESETS = [
  { key: 'royal',   label: 'Royal blauw',   bg1: '#002FA7', bg2: '#001A5C', accent: '#FEE68A' },
  { key: 'sunset',  label: 'Zonsondergang', bg1: '#B91C1C', bg2: '#3F0000', accent: '#FED7AA' },
  { key: 'forest',  label: 'Forest',        bg1: '#065F46', bg2: '#022C22', accent: '#FEF3C7' },
  { key: 'slate',   label: 'Slate',         bg1: '#1F2937', bg2: '#0B0F17', accent: '#FDE68A' },
  { key: 'rose',    label: 'Rose goud',     bg1: '#9F1239', bg2: '#450A0A', accent: '#FFE4E6' },
  { key: 'midnight',label: 'Midnight',      bg1: '#312E81', bg2: '#0B0B3A', accent: '#C7D2FE' },
];

export default function KassaDisplayPanel() {
  const [form, setForm] = useState({
    kassa_display_welkom: 'Welkom!',
    kassa_display_bedank: 'Bedankt voor uw bezoek!',
    kassa_display_bg1: '#002FA7',
    kassa_display_bg2: '#001A5C',
    kassa_display_accent: '#FEE68A',
    kassa_display_banners: [],
    kassa_display_banner_interval: 6,
    kassa_display_toon_logo: true,
    kassa_display_toon_klok: true,
  });
  const [displayCode, setDisplayCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (v) => setForm(s => ({ ...s, [k]: typeof v === 'object' && v?.target ? v.target.value : v }));

  useEffect(() => {
    API.get('/instellingen').then(r => {
      const d = r.data || {};
      setForm(s => ({
        ...s,
        kassa_display_welkom: d.kassa_display_welkom ?? s.kassa_display_welkom,
        kassa_display_bedank: d.kassa_display_bedank ?? s.kassa_display_bedank,
        kassa_display_bg1: d.kassa_display_bg1 || s.kassa_display_bg1,
        kassa_display_bg2: d.kassa_display_bg2 || s.kassa_display_bg2,
        kassa_display_accent: d.kassa_display_accent || s.kassa_display_accent,
        kassa_display_banners: Array.isArray(d.kassa_display_banners) ? d.kassa_display_banners : [],
        kassa_display_banner_interval: d.kassa_display_banner_interval || 6,
        kassa_display_toon_logo: d.kassa_display_toon_logo ?? true,
        kassa_display_toon_klok: d.kassa_display_toon_klok ?? true,
      }));
    }).catch(() => {});
    API.post('/kassa/display/init').then(r => setDisplayCode(r.data.code)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await API.put('/instellingen', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  const addBanners = (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    Promise.all(arr.map(f => new Promise((res, rej) => {
      if (!f.type.startsWith('image/')) return rej(new Error('Geen afbeelding'));
      if (f.size > 1_500_000) return rej(new Error(`${f.name}: > 1.5 MB (te groot)`));
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = () => rej(r.error);
      r.readAsDataURL(f);
    })))
      .then(urls => setForm(s => ({ ...s, kassa_display_banners: [...(s.kassa_display_banners || []), ...urls] })))
      .catch(e => alert(e.message));
  };

  const delBanner = (idx) => setForm(s => ({ ...s, kassa_display_banners: (s.kassa_display_banners || []).filter((_, i) => i !== idx) }));

  const applyPreset = (p) => setForm(s => ({ ...s, kassa_display_bg1: p.bg1, kassa_display_bg2: p.bg2, kassa_display_accent: p.accent }));

  const displayURL = displayCode ? `${window.location.origin}/klant-display/${displayCode}` : '';
  const previewStyle = {
    background: `linear-gradient(135deg, ${form.kassa_display_bg1} 0%, ${form.kassa_display_bg2} 100%)`,
    color: '#fff',
  };

  return (
    <div className="space-y-5" data-testid="kassa-display-panel">
      <div className="flex items-start gap-4 p-4 rounded-lg border border-blue-200" style={{ background: 'linear-gradient(90deg,#EFF6FF 0%,#EEF2FF 100%)' }}>
        <div className="p-2 rounded bg-white border border-blue-200"><Monitor size={28} className="text-[#002FA7]" /></div>
        <div className="flex-1">
          <div className="font-bold text-[#002FA7] mb-1">Klant-display (2e scherm)</div>
          <div className="text-xs text-gray-700 mb-2">Publiek scherm voor aan de kassa: toont artikels, totaal en een SEPA-betaal-QR. Open de link op een tablet of monitor en laat het altijd openstaan.</div>
          {displayURL && (
            <div className="flex items-center gap-2 mt-2">
              <code className="text-[11px] font-mono px-2 py-1 bg-white border border-gray-200 rounded break-all flex-1">{displayURL}</code>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => navigator.clipboard?.writeText(displayURL)} data-testid="inst-kd-copy"><Copy size={12} /> Kopieer</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => window.open(displayURL, '_blank', 'noopener')} data-testid="inst-kd-open"><ArrowSquareOut size={12} /> Openen</button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LINKS: velden */}
        <div className="space-y-5">
          {/* Teksten */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Teksten</span></div>
            <div className="section-body space-y-3">
              <div>
                <label className="form-label">Welkomstekst (idle-scherm)</label>
                <input
                  className="field-input"
                  value={form.kassa_display_welkom}
                  onChange={update('kassa_display_welkom')}
                  placeholder="Welkom!"
                  data-testid="kd-welkom"
                />
                <p className="text-[10px] text-gray-400 mt-1">Verschijnt wanneer er geen klant wordt afgerekend.</p>
              </div>
              <div>
                <label className="form-label">Bedanktekst (na betaling)</label>
                <input
                  className="field-input"
                  value={form.kassa_display_bedank}
                  onChange={update('kassa_display_bedank')}
                  placeholder="Bedankt voor uw bezoek!"
                  data-testid="kd-bedank"
                />
                <p className="text-[10px] text-gray-400 mt-1">Verschijnt op het Betaald-scherm gedurende 8 seconden.</p>
              </div>
            </div>
          </div>

          {/* Kleurenschema */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Kleurenschema</span></div>
            <div className="section-body space-y-3">
              <div>
                <label className="form-label mb-2">Voorinstellingen</label>
                <div className="grid grid-cols-3 gap-2">
                  {COLOR_PRESETS.map(p => (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => applyPreset(p)}
                      className="p-2 rounded border border-gray-200 hover:border-gray-400 text-left transition"
                      style={{ background: `linear-gradient(135deg, ${p.bg1} 0%, ${p.bg2} 100%)`, color: '#fff' }}
                      data-testid={`kd-preset-${p.key}`}
                    >
                      <div className="text-[11px] font-bold">{p.label}</div>
                      <div className="flex gap-1 mt-1">
                        <span className="w-3 h-3 rounded-full border border-white/50" style={{ background: p.bg1 }} />
                        <span className="w-3 h-3 rounded-full border border-white/50" style={{ background: p.bg2 }} />
                        <span className="w-3 h-3 rounded-full border border-white/50" style={{ background: p.accent }} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <ColorField label="Achtergrond 1" value={form.kassa_display_bg1} onChange={update('kassa_display_bg1')} testid="kd-bg1" />
                <ColorField label="Achtergrond 2" value={form.kassa_display_bg2} onChange={update('kassa_display_bg2')} testid="kd-bg2" />
                <ColorField label="Accentkleur" value={form.kassa_display_accent} onChange={update('kassa_display_accent')} testid="kd-accent" />
              </div>
              <p className="text-[10px] text-gray-400">Accentkleur wordt gebruikt voor het grote te-betalen-bedrag tijdens de betaling.</p>
            </div>
          </div>

          {/* Weergave-opties */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Weergave-opties</span></div>
            <div className="section-body space-y-3">
              <IOSToggle
                label="Bedrijfslogo tonen"
                checked={form.kassa_display_toon_logo}
                onChange={update('kassa_display_toon_logo')}
                testid="kd-toon-logo"
              />
              <IOSToggle
                label="Klok tonen (rechtsboven)"
                checked={form.kassa_display_toon_klok}
                onChange={update('kassa_display_toon_klok')}
                testid="kd-toon-klok"
              />
            </div>
          </div>
        </div>

        {/* RECHTS: banners + preview */}
        <div className="space-y-5">
          {/* Reclamebanners */}
          <div className="section-card">
            <div className="section-header">
              <span className="section-title">Reclamebanners (idle-modus)</span>
              <span className="text-[10px] text-gray-400">{(form.kassa_display_banners || []).length} geüpload</span>
            </div>
            <div className="section-body space-y-3">
              <p className="text-xs text-gray-600">Afbeeldingen die afwisselend getoond worden op het welkomstscherm — ideaal voor promoties of nieuwigheden. Wordt niet getoond tijdens afrekenen.</p>
              <div className="grid grid-cols-2 gap-2">
                {(form.kassa_display_banners || []).map((b, i) => (
                  <div key={i} className="relative group aspect-video rounded border border-gray-200 overflow-hidden bg-gray-50">
                    <img src={b} alt={`Banner ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-white/90 hover:bg-red-500 hover:text-white p-1 rounded shadow opacity-0 group-hover:opacity-100 transition"
                      onClick={() => delBanner(i)}
                      data-testid={`kd-banner-del-${i}`}
                      title="Verwijder"
                    ><Trash size={12} /></button>
                    <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] font-mono px-1 rounded">#{i + 1}</div>
                  </div>
                ))}
                <button
                  type="button"
                  className="aspect-video rounded border-2 border-dashed border-gray-300 hover:border-[#002FA7] hover:bg-blue-50 flex flex-col items-center justify-center text-gray-400 hover:text-[#002FA7] transition"
                  onClick={() => fileRef.current?.click()}
                  data-testid="kd-banner-add"
                >
                  <Plus size={20} />
                  <span className="text-xs mt-1">Upload afbeelding(en)</span>
                  <span className="text-[9px]">max 1.5 MB per stuk</span>
                </button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={e => { addBanners(e.target.files); e.target.value = ''; }}
              />
              {(form.kassa_display_banners || []).length > 1 && (
                <div>
                  <label className="form-label">Rotatie-interval (seconden)</label>
                  <input
                    type="number"
                    min="2"
                    max="60"
                    className="field-input"
                    style={{ maxWidth: 140 }}
                    value={form.kassa_display_banner_interval}
                    onChange={e => update('kassa_display_banner_interval')(parseInt(e.target.value) || 6)}
                    data-testid="kd-interval"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="section-card">
            <div className="section-header"><span className="section-title">Live voorbeeld (idle-scherm)</span></div>
            <div className="section-body">
              <div className="rounded overflow-hidden" style={{ ...previewStyle, aspectRatio: '16/9', position: 'relative' }}>
                <div className="absolute inset-0 flex items-center justify-center text-center p-6">
                  <div>
                    <div className="text-5xl mb-3">🛒</div>
                    <div className="text-2xl font-bold">{form.kassa_display_welkom || 'Welkom!'}</div>
                    <div className="text-xs opacity-70 mt-2">Klaar om u te helpen</div>
                  </div>
                </div>
                <div className="absolute top-2 right-2 text-[10px] uppercase tracking-wider opacity-60">klant-display</div>
                <div className="absolute bottom-2 left-3 text-xs font-bold opacity-80">
                  Preview · te-betalen-kleur:
                  <span className="inline-block w-3 h-3 rounded-full ml-1 align-middle border border-white/30" style={{ background: form.kassa_display_accent }} />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Dit is enkel een snelpreview. Open het echte display om alle schermen (shopping/paying/done) te zien.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500">Wijzigingen verschijnen automatisch op het display na opslaan.</div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Opgeslagen</span>}
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={busy}
            data-testid="kd-save"
          >{busy ? 'Opslaan…' : 'Opslaan'}</button>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="form-label">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="color"
          value={value || '#000000'}
          onChange={e => onChange(e.target.value)}
          className="w-10 h-9 rounded border border-gray-200 cursor-pointer shrink-0"
          data-testid={testid}
        />
        <input
          className="field-input text-xs font-mono"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="#RRGGBB"
        />
      </div>
    </div>
  );
}
